import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, UploadCloud, FileText, Trash2, Sparkles, Plus,
  CheckCircle2, Loader2, Rocket, Info,
} from "lucide-react";
import { toast } from "sonner";
import { ciApi } from "../../components/ci/ciApi";
import { Panel, Kicker, ConfBadge } from "../../components/ci/ciWidgets";

const STEPS = ["Identify", "Sources", "Research", "Peers", "Configure"];

const OWNERSHIP = ["Public listed", "Private", "PE / VC-owned", "Family-owned", "State-owned", "Cooperative", "Subsidiary"];
const REVENUE_RANGES = ["< $10M", "$10M – $100M", "$100M – $1B", "$1B – $10B", "$10B – $50B", "> $50B", "Not disclosed"];
const EMPLOYEE_RANGES = ["< 100", "100 – 1,000", "1,000 – 10,000", "10,000 – 50,000", "50,000 – 100,000", "> 100,000", "Not disclosed"];
const PEER_CATEGORIES = [
  ["closest", "Closest comparable"],
  ["regional", "Regional peer"],
  ["industry_median", "Industry median"],
  ["global_top", "Global top-quartile"],
];

const inputCls =
  "w-full rounded-lg bg-[#060d18] border border-white/15 px-3.5 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-teal-400/60 transition-colors";
const labelCls = "block text-[11px] tracking-[0.14em] uppercase font-semibold text-white/50 mb-1.5";

const Field = ({ label, children, required }) => (
  <div>
    <label className={labelCls}>
      {label} {required && <span className="text-amber-300">*</span>}
    </label>
    {children}
  </div>
);

export default function CINewAssessment() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(0);
  const [assessmentId, setAssessmentId] = useState(null);
  const [busy, setBusy] = useState(false);

  const [company, setCompany] = useState({
    name: "", legal_name: "", ticker: "", website: "", headquarters: "",
    ownership_type: "", listing_status: "", industry: "", sub_sector: "",
    revenue_range: "", employee_range: "", geographies: "",
    reporting_currency: "USD", reporting_period: "",
  });
  const [purpose, setPurpose] = useState("Board review");
  const [audience, setAudience] = useState("Board and CXOs");
  const [analystNotes, setAnalystNotes] = useState("");

  const [sources, setSources] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [peers, setPeers] = useState([]);
  const [recommending, setRecommending] = useState(false);
  const [newPeer, setNewPeer] = useState({ name: "", category: "closest" });

  const [weights, setWeights] = useState({});
  const [depth, setDepth] = useState("comprehensive");
  const [perspective, setPerspective] = useState("board");
  const [scenario, setScenario] = useState("balanced");

  useEffect(() => {
    ciApi.get("/meta").then((r) => {
      setMeta(r.data);
      setWeights({ ...r.data.default_weights });
    });
  }, []);

  const set = (k) => (e) => setCompany((c) => ({ ...c, [k]: e.target.value }));

  const weightTotal = useMemo(
    () => Object.values(weights).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [weights]
  );

  // ---------------- step actions

  const saveStep1 = async () => {
    if (!company.name.trim() || !company.industry.trim()) {
      toast.error("Company name and industry are required");
      return;
    }
    setBusy(true);
    try {
      if (!assessmentId) {
        const r = await ciApi.post("/assessments", { company, purpose, audience, analyst_notes: analystNotes });
        setAssessmentId(r.data.id);
      } else {
        await ciApi.patch(`/assessments/${assessmentId}`, { company, purpose, audience });
      }
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save the company profile");
    } finally {
      setBusy(false);
    }
  };

  const uploadPending = async () => {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    for (const pf of pendingFiles) {
      const form = new FormData();
      form.append("file", pf.file);
      form.append("category", pf.category);
      try {
        const r = await ciApi.post(`/assessments/${assessmentId}/sources`, form);
        setSources((s) => [...s, r.data]);
      } catch (e) {
        toast.error(`${pf.file.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    setPendingFiles([]);
    setUploading(false);
  };

  const deleteSource = async (id) => {
    await ciApi.delete(`/assessments/${assessmentId}/sources/${id}`);
    setSources((s) => s.filter((x) => x.id !== id));
  };

  const saveNotes = async () => {
    await ciApi.patch(`/assessments/${assessmentId}`, { analyst_notes: analystNotes });
    setStep(3);
  };

  const recommendPeers = async () => {
    setRecommending(true);
    try {
      const r = await ciApi.post(`/assessments/${assessmentId}/peers/recommend`);
      setPeers(r.data.peers);
      toast.success(`${r.data.peers.length} peers recommended across 4 benchmark groups`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Peer recommendation failed");
    } finally {
      setRecommending(false);
    }
  };

  const savePeers = async () => {
    setBusy(true);
    try {
      await ciApi.put(`/assessments/${assessmentId}/peers`, { peers, peers_approved: true });
      setStep(4);
    } catch (e) {
      toast.error("Failed to save the peer set");
    } finally {
      setBusy(false);
    }
  };

  const launch = async () => {
    if (Math.abs(weightTotal - 100) > 0.01) {
      toast.error(`Weights must total 100% — currently ${weightTotal}%`);
      return;
    }
    setBusy(true);
    try {
      await ciApi.patch(`/assessments/${assessmentId}`, {
        config: { weights, depth, output_format: "board_pack", perspective, scenario },
      });
      await ciApi.post(`/assessments/${assessmentId}/analyze`);
      toast.success("Multi-agent analysis launched");
      navigate(`/company-intelligence/workspace/${assessmentId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to launch the analysis");
      setBusy(false);
    }
  };

  // ---------------- render helpers

  const Stepper = () => (
    <div className="flex items-center gap-2 mb-10 overflow-x-auto" data-testid="ci-wizard-stepper">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <button
            onClick={() => i < step && setStep(i)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors ${
              i === step
                ? "bg-teal-400/12 text-teal-300 border border-teal-400/35"
                : i < step
                ? "text-emerald-300 hover:bg-white/5"
                : "text-white/35"
            }`}
          >
            {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="font-serif tabular-nums">{i + 1}</span>}
            {s}
          </button>
          {i < STEPS.length - 1 && <div className="w-6 h-px bg-white/15 shrink-0" />}
        </React.Fragment>
      ))}
    </div>
  );

  if (!meta)
    return (
      <div className="flex justify-center py-24 text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div className="max-w-4xl mx-auto" data-testid="ci-wizard-page">
        <Kicker className="mb-3">New Company Assessment</Kicker>
        <h1 className="font-serif text-3xl sm:text-4xl mb-8">
          {company.name ? company.name : "Set up the assessment"}
        </h1>
        <Stepper />

        {/* ============ STEP 1 — Identification ============ */}
        {step === 0 && (
          <Panel className="p-8" data-testid="ci-step-identify">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Company name" required>
                <input className={inputCls} value={company.name} onChange={set("name")} placeholder="e.g. BHP Group" data-testid="ci-company-name-input" />
              </Field>
              <Field label="Legal entity name">
                <input className={inputCls} value={company.legal_name} onChange={set("legal_name")} placeholder="e.g. BHP Group Limited" />
              </Field>
              <Field label="Stock ticker">
                <input className={inputCls} value={company.ticker} onChange={set("ticker")} placeholder="e.g. ASX: BHP (if listed)" />
              </Field>
              <Field label="Website">
                <input className={inputCls} value={company.website} onChange={set("website")} placeholder="https://…" />
              </Field>
              <Field label="Headquarters">
                <input className={inputCls} value={company.headquarters} onChange={set("headquarters")} placeholder="City, country" />
              </Field>
              <Field label="Ownership type">
                <select className={inputCls} value={company.ownership_type} onChange={set("ownership_type")}>
                  <option value="">Select…</option>
                  {OWNERSHIP.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="Public / private status">
                <select className={inputCls} value={company.listing_status} onChange={set("listing_status")}>
                  <option value="">Select…</option>
                  <option>Public — listed</option>
                  <option>Private</option>
                  <option>Public — unlisted</option>
                </select>
              </Field>
              <Field label="Industry" required>
                <select className={inputCls} value={company.industry} onChange={set("industry")} data-testid="ci-industry-select">
                  <option value="">Select…</option>
                  {meta.industries.map((i) => (
                    <option key={i.key} value={i.label}>
                      {i.label}
                    </option>
                  ))}
                  <option>Other / Diversified</option>
                </select>
              </Field>
              <Field label="Sub-sector">
                <input className={inputCls} value={company.sub_sector} onChange={set("sub_sector")} placeholder="e.g. Iron ore & copper mining" />
              </Field>
              <Field label="Revenue range">
                <select className={inputCls} value={company.revenue_range} onChange={set("revenue_range")}>
                  <option value="">Select…</option>
                  {REVENUE_RANGES.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="Employee range">
                <select className={inputCls} value={company.employee_range} onChange={set("employee_range")}>
                  <option value="">Select…</option>
                  {EMPLOYEE_RANGES.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="Primary geographies">
                <input className={inputCls} value={company.geographies} onChange={set("geographies")} placeholder="e.g. Australia, Americas, Asia" />
              </Field>
              <Field label="Reporting currency">
                <input className={inputCls} value={company.reporting_currency} onChange={set("reporting_currency")} placeholder="USD" />
              </Field>
              <Field label="Reporting period">
                <input className={inputCls} value={company.reporting_period} onChange={set("reporting_period")} placeholder="e.g. FY2025 (Jul 2024 – Jun 2025)" />
              </Field>
              <Field label="Assessment purpose" required>
                <select className={inputCls} value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid="ci-purpose-select">
                  {meta.purposes.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </Field>
              <Field label="Intended audience">
                <input className={inputCls} value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g. Board and CXOs" />
              </Field>
            </div>
            <div className="flex justify-end mt-8">
              <button
                onClick={saveStep1}
                disabled={busy || !company.name.trim() || !company.industry.trim()}
                data-testid="ci-step1-next-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 disabled:opacity-40 text-[#06251f] font-bold text-sm px-7 py-3 transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Continue to sources <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Panel>
        )}

        {/* ============ STEP 2 — Sources ============ */}
        {step === 1 && (
          <Panel className="p-8" data-testid="ci-step-sources">
            <div className="flex items-start gap-3 rounded-lg border border-teal-400/20 bg-teal-400/[0.05] px-4 py-3 mb-6 text-[13px] text-white/65">
              <Info className="w-4 h-4 text-teal-300 shrink-0 mt-0.5" />
              Upload annual reports, financial statements, investor presentations, sustainability reports, regulatory
              filings, strategy documents, prior AI-generated reports or interview notes. PDF, DOCX, TXT, CSV and XLSX
              are supported (max 25&nbsp;MB each). AI-generated reports are treated as hypotheses until verified.
            </div>

            <label
              className="block border-2 border-dashed border-white/15 hover:border-teal-400/50 rounded-xl px-6 py-10 text-center cursor-pointer transition-colors"
              data-testid="ci-upload-dropzone"
            >
              <UploadCloud className="w-8 h-8 text-teal-300 mx-auto mb-3" />
              <div className="text-sm text-white/70 font-medium">Click to select files</div>
              <div className="text-[12px] text-white/35 mt-1">You can add multiple documents and categorise each one</div>
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.txt,.csv,.xlsx,.md"
                className="hidden"
                onChange={(e) =>
                  setPendingFiles((p) => [
                    ...p,
                    ...Array.from(e.target.files).map((f) => ({ file: f, category: "Annual report" })),
                  ])
                }
              />
            </label>

            {pendingFiles.length > 0 && (
              <div className="mt-6 space-y-3">
                <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/50">Ready to upload</div>
                {pendingFiles.map((pf, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-lg border border-white/10 bg-[#060d18] px-4 py-3">
                    <FileText className="w-4 h-4 text-white/40 shrink-0" />
                    <span className="text-sm text-white/80 truncate flex-1">{pf.file.name}</span>
                    <select
                      className="rounded bg-[#0c1626] border border-white/15 text-[12px] text-white/70 px-2 py-1.5"
                      value={pf.category}
                      onChange={(e) =>
                        setPendingFiles((p) => p.map((x, xi) => (xi === i ? { ...x, category: e.target.value } : x)))
                      }
                    >
                      {meta.source_categories.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                    <button onClick={() => setPendingFiles((p) => p.filter((_, xi) => xi !== i))} className="text-white/30 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={uploadPending}
                  disabled={uploading}
                  data-testid="ci-upload-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                  {uploading ? "Extracting text…" : `Upload ${pendingFiles.length} file(s)`}
                </button>
              </div>
            )}

            {sources.length > 0 && (
              <div className="mt-6 space-y-2">
                <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/50">Uploaded &amp; classified</div>
                {sources.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.04] px-4 py-3" data-testid={`ci-source-${s.id}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" />
                    <span className="text-sm text-white/85 truncate flex-1">{s.filename}</span>
                    <span className="text-[11px] text-teal-300 border border-teal-400/25 rounded-full px-2 py-0.5">{s.category}</span>
                    <span className="text-[11px] text-white/35 tabular-nums">{(s.text_chars || 0).toLocaleString()} chars</span>
                    <button onClick={() => deleteSource(s.id)} className="text-white/30 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between mt-8">
              <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={() => setStep(2)}
                data-testid="ci-step2-next-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 text-[#06251f] font-bold text-sm px-7 py-3 transition-colors"
              >
                {sources.length === 0 ? "Continue without documents" : "Continue to research"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Panel>
        )}

        {/* ============ STEP 3 — Research ============ */}
        {step === 2 && (
          <Panel className="p-8" data-testid="ci-step-research">
            <h2 className="font-serif text-xl mb-4">Public research &amp; evidence policy</h2>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              The agents combine your uploaded documents with credible public knowledge. The evidence hierarchy below
              governs what counts as a primary source — AI summaries, blogs and promotional material are never treated
              as evidence.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {[
                ["Primary sources", "Audited financials, exchange & regulatory filings, investor-relations material, sustainability reports"],
                ["Credible secondary", "Government databases, regulators, recognised industry associations, reputable financial databases"],
                ["Signals & context", "Credible analyst reports, business publications, job postings, technology-partner announcements, patents"],
                ["Hypotheses only", "Uploaded ChatGPT / Claude / Gemini / Kimi / Perplexity reports — synthesised but verified before use"],
              ].map(([t, d]) => (
                <div key={t} className="rounded-lg border border-white/10 bg-[#060d18] p-4">
                  <div className="text-[12px] font-bold text-teal-300 mb-1">{t}</div>
                  <div className="text-[12px] text-white/55 leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
            <Field label="Analyst / management notes (optional — passed to every agent)">
              <textarea
                className={`${inputCls} min-h-[110px]`}
                value={analystNotes}
                onChange={(e) => setAnalystNotes(e.target.value)}
                placeholder="e.g. Focus on the copper growth strategy; management flagged working-capital pressure in H2…"
                data-testid="ci-analyst-notes-input"
              />
            </Field>
            <div className="flex justify-between mt-8">
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={saveNotes}
                data-testid="ci-step3-next-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 text-[#06251f] font-bold text-sm px-7 py-3 transition-colors"
              >
                Continue to peers <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Panel>
        )}

        {/* ============ STEP 4 — Peers ============ */}
        {step === 3 && (
          <Panel className="p-8" data-testid="ci-step-peers">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-serif text-xl mb-1">Peer confirmation</h2>
                <p className="text-[13px] text-white/55">
                  Four benchmark groups: closest comparable · regional · industry median · global top-quartile.
                </p>
              </div>
              <button
                onClick={recommendPeers}
                disabled={recommending}
                data-testid="ci-recommend-peers-btn"
                className="inline-flex items-center gap-2 rounded-full bg-amber-400/15 hover:bg-amber-400/25 border border-amber-400/40 text-amber-200 text-sm font-semibold px-5 py-2.5 transition-colors"
              >
                {recommending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {recommending ? "Selecting credible peers…" : peers.length ? "Re-recommend peers" : "Recommend peers with AI"}
              </button>
            </div>

            {peers.length === 0 && !recommending && (
              <div className="text-sm text-white/40 italic text-center py-8">
                No peers yet — use the AI recommendation or add peers manually below.
              </div>
            )}

            <div className="space-y-3">
              {peers.map((p, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-[#060d18] px-4 py-3.5" data-testid={`ci-peer-row-${i}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-semibold text-white flex-1 min-w-[140px]">{p.name}</span>
                    <select
                      className="rounded bg-[#0c1626] border border-white/15 text-[12px] text-white/70 px-2 py-1.5"
                      value={p.category}
                      onChange={(e) => setPeers((ps) => ps.map((x, xi) => (xi === i ? { ...x, category: e.target.value } : x)))}
                    >
                      {PEER_CATEGORIES.map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                    <ConfBadge level={p.confidence} />
                    <button onClick={() => setPeers((ps) => ps.filter((_, xi) => xi !== i))} className="text-white/30 hover:text-red-300" data-testid={`ci-peer-remove-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {(p.reason || p.differences) && (
                    <div className="text-[12px] text-white/45 mt-2 leading-relaxed">
                      {p.reason} {p.differences && <span className="text-white/35">· Differences: {p.differences}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-5">
              <input
                className={`${inputCls} flex-1`}
                placeholder="Add a peer manually…"
                value={newPeer.name}
                onChange={(e) => setNewPeer((n) => ({ ...n, name: e.target.value }))}
                data-testid="ci-add-peer-input"
              />
              <select
                className="rounded-lg bg-[#060d18] border border-white/15 text-[12px] text-white/70 px-2 py-2.5"
                value={newPeer.category}
                onChange={(e) => setNewPeer((n) => ({ ...n, category: e.target.value }))}
              >
                {PEER_CATEGORIES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (!newPeer.name.trim()) return;
                  setPeers((ps) => [...ps, { ...newPeer, reason: "Added by analyst", similarities: "", differences: "", data_availability: "medium", confidence: "medium", approved: true }]);
                  setNewPeer({ name: "", category: "closest" });
                }}
                data-testid="ci-add-peer-btn"
                className="p-2.5 rounded-lg border border-white/20 text-white/60 hover:text-teal-300 hover:border-teal-400/50 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            <div className="flex justify-between mt-8">
              <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={savePeers}
                disabled={busy}
                data-testid="ci-step4-next-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 disabled:opacity-40 text-[#06251f] font-bold text-sm px-7 py-3 transition-colors"
              >
                {peers.length ? "Approve peer set & continue" : "Continue without peers"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Panel>
        )}

        {/* ============ STEP 5 — Configure ============ */}
        {step === 4 && (
          <Panel className="p-8" data-testid="ci-step-configure">
            <h2 className="font-serif text-xl mb-6">Assessment configuration</h2>

            <div className="grid sm:grid-cols-3 gap-5 mb-8">
              <Field label="Reporting depth">
                <select className={inputCls} value={depth} onChange={(e) => setDepth(e.target.value)}>
                  <option value="executive">Executive summary</option>
                  <option value="standard">Standard</option>
                  <option value="comprehensive">Comprehensive board pack</option>
                </select>
              </Field>
              <Field label="Perspective">
                <select className={inputCls} value={perspective} onChange={(e) => setPerspective(e.target.value)} data-testid="ci-perspective-select">
                  <option value="board">Board / Independent Director</option>
                  <option value="investor">Investor</option>
                  <option value="consultant">Consultant</option>
                  <option value="management">Management</option>
                </select>
              </Field>
              <Field label="Scenario stance">
                <select className={inputCls} value={scenario} onChange={(e) => setScenario(e.target.value)}>
                  <option value="conservative">Conservative</option>
                  <option value="balanced">Balanced</option>
                  <option value="opportunity">Opportunity-oriented</option>
                </select>
              </Field>
            </div>

            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/50">Dimension weights</div>
              <div className="flex items-center gap-3">
                <span
                  className={`text-sm font-bold tabular-nums ${Math.abs(weightTotal - 100) < 0.01 ? "text-emerald-300" : "text-red-300"}`}
                  data-testid="ci-weight-total"
                >
                  Σ {weightTotal}%
                </span>
                <button
                  onClick={() => setWeights({ ...meta.default_weights })}
                  className="text-[12px] text-white/45 hover:text-teal-300 underline underline-offset-2 transition-colors"
                  data-testid="ci-reset-weights-btn"
                >
                  Reset to industry defaults
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mb-8">
              {meta.dimensions.map((d) => (
                <div key={d.key} className="flex items-center gap-3">
                  <span className="text-[13px] text-white/70 flex-1">{d.name}</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={weights[d.key] ?? 0}
                    onChange={(e) => setWeights((w) => ({ ...w, [d.key]: parseFloat(e.target.value) || 0 }))}
                    className="w-20 rounded bg-[#060d18] border border-white/15 px-2 py-1.5 text-sm text-white text-right tabular-nums focus:outline-none focus:border-teal-400/60"
                    data-testid={`ci-weight-${d.key}`}
                  />
                  <span className="text-white/35 text-sm">%</span>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-teal-400/25 bg-teal-400/[0.05] px-4 py-3 text-[12px] text-teal-100/80 mb-8">
              Launching runs 15 AI agents (~4–8 minutes). Scores are computed programmatically from agent evidence,
              the Leadership Quadrant is generated automatically, and residual data gaps are consolidated in the closing annexure.
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(3)} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={launch}
                disabled={busy || Math.abs(weightTotal - 100) > 0.01}
                data-testid="ci-launch-analysis-btn"
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#231a03] font-bold text-sm px-8 py-3.5 transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                Launch multi-agent analysis
              </button>
            </div>
          </Panel>
        )}
      </div>
  );
}
